import { FC, useEffect, useMemo, useState } from 'react';
import {
  BackHeader,
  Button,
  Center,
  Icon,
  LevelAndStageCelebrations,
  LinkButton,
  Page,
} from 'src/components';
import { FloatingMiniPlayer } from 'src/components/FloatingMiniPlayer';
import Head from 'next/head';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { BuddyApi, EligibilityApi, MeApi, UserScenarioApi } from 'src/api';
import {
  PointsResponse,
  ProgressResponse,
  RecommendationsActivityType,
  RecommendationsResponse,
} from 'src/api/me/types';
import { LearnablesList } from 'src/apps/ScenarioGeneric/LearnablesList';
import { ScenarioHeader } from 'src/apps/ScenarioGeneric/ScenarioHeader';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { ActivitySource } from 'src/utils/analytics/analytics.const';
import { SessionType } from 'src/api/learningSessions/types';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { EligibilityResponse } from 'src/api/eligibility/types';
import { FIVE_MINUTES } from 'src/constants/times.const';
import { BuddiesResponse } from 'src/api/buddy/types';
import { UserScenarioLearnablesResponse } from 'src/api/userScenario/types';
import { useLatestPointsAndProgress } from 'src/hooks/useLatestPointsAndProgress';
import { useUserContext } from 'src/context/user';
import { BuddyRecommendationModal } from 'src/apps/Buddies/BuddyRecommendationModal';
import { SessionCompletedCelebration } from 'src/apps/LearningSession/EndOfSession/SessionCompletedCelebration';
import { ScenarioSummary } from 'src/apps/LearningSession/EndOfSession/ScenarioSummary';
import * as S from 'src/apps/LearningSession/EndOfSession/EndOfSessionLearn2024/styles';
import { compare } from 'src/apps/LearningSession/EndOfSession/EndOfSessionLearn2024/utils';
import { Activities } from 'src/components/MiniPlayer';
import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { randomSign } from 'src/utils/numbers';
import { EOS_PLANS_PAGE } from '../index';

const msgs = defineMessages({
  title: {
    id: 'scenario_end_of_session_title',
  },
  backToHome: {
    id: 'web_ea_eos_words_back_to_home',
  },
  scenarioComplete: {
    id: 'web_ea_eos_scenario_completed',
  },
  continue: {
    id: 'Continue',
  },
  learningSettings: {
    id: 'web_ls_menu',
  },
  buddyButton: {
    id: 'ai_buddies_sentence_builder_button',
  },
});

type EndOfSessionLearnProps = {
  levelName: string;
  iconSrc: string;
  hasUserScenario: boolean;
  isPremiumScenario: boolean;
  userScenarioCompleted?: boolean;
  progressBefore?: ProgressResponse | null;
  pointsBefore?: PointsResponse | null;
  languageName: string;
};

export const EndOfSessionLearn2024: FC<EndOfSessionLearnProps> = ({
  levelName,
  iconSrc,
  hasUserScenario,
  isPremiumScenario,
  userScenarioCompleted,
  progressBefore,
  pointsBefore,
  languageName,
}) => {
  const router = useRouter();
  const { safePush } = useSafePush();
  const languagePairId = getLanguagePairId();
  const { user } = useUserContext();

  const [activity, setActivity] = useState<RecommendationsActivityType>();
  const [hasClickedContinue, setHasClickedContinue] = useState<boolean>(false);
  const [showSessionCompletedCelebration, setShowSessionCompletedCelebration] = useState(true);
  const [showLevelAndStageCelebrations, setShowLevelAndStageCelebrations] = useState(false);
  const [showedPlansPage] = useState(getLocalStorageEntry(EOS_PLANS_PAGE) !== 'yes');
  const [showedBuddyRecommendation, setShowedBuddyRecommendation] = useState(false);

  const {
    contentMediaId,
    scenario_id: scenarioId,
    back,
    pathway_node_id: pathwayNodeId,
  } = router.query;
  const backUrl = back ? String(back) : undefined;
  const shouldReturnToPath = backUrl === ROUTES.path || typeof pathwayNodeId === 'string';
  const hasCustomRecommendation = !!(contentMediaId && userScenarioCompleted);

  const { pointsAfter, progressAfter, isLoadingPointsAndProgress } = useLatestPointsAndProgress(
    languagePairId,
    pointsBefore,
  );

  const { data: eligibility, isLoading: isLoadingEligibility } =
    useQuery<EligibilityResponse | null>({
      queryKey: ['eligibility', languagePairId],
      queryFn: () => {
        /* istanbul ignore if: should be defined */
        if (!languagePairId) return null;
        return EligibilityApi.getEligibility(languagePairId);
      },
      gcTime: FIVE_MINUTES,
      staleTime: FIVE_MINUTES,
    });

  const randomizedShouldShow = useMemo(() => randomSign() > 0, []);
  const showBuddyRecommendation = Boolean(
    !showedBuddyRecommendation &&
    eligibility?.eligibleForAiBuddies &&
    (user?.isPro ? randomizedShouldShow : !showedPlansPage),
  );

  const { data: scenarioDetails, isLoading: isLoadingScenarioDetails } =
    useQuery<UserScenarioLearnablesResponse | null>({
      queryKey: ['userScenarioLearnables', scenarioId],
      queryFn: () => {
        /* istanbul ignore if: should be defined */
        if (!scenarioId) return null;
        return UserScenarioApi.getUserScenarioDetails(Number(scenarioId));
      },
      enabled: !!scenarioId,
    });

  const sortedLearnables = useMemo(() => {
    return scenarioDetails?.learnables.sort(compare) || [];
  }, [scenarioDetails]);

  const { data: buddiesData, isLoading: isLoadingBuddies } = useQuery<BuddiesResponse | null>({
    queryKey: ['buddies', languagePairId],
    queryFn: () => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId) return null;
      return BuddyApi.getAll(languagePairId);
    },
    enabled: showBuddyRecommendation,
  });

  const { data: recommendations, isFetching: isFetchingRecommendations } =
    useQuery<RecommendationsResponse | null>({
      queryKey: ['getRecommendations', languagePairId],
      queryFn: () => {
        /* istanbul ignore if: should be defined */
        if (!languagePairId) return null;
        return MeApi.getRecommendations(languagePairId);
      },
      enabled: hasClickedContinue,
    });

  useEffect(() => {
    if (hasCustomRecommendation) {
      setActivity('immerse');
    } else {
      recommendations && setActivity(recommendations.recommendationOrdering[0]);
    }
  }, [recommendations, hasCustomRecommendation]);

  const intl = useIntl();

  const learnCompletedMessage = msgs.scenarioComplete;

  const supportedActivities: Activities[] = [
    'learn',
    ...(eligibility?.eligibleForImmerse ? ['immerse'] : []),
    ...(eligibility?.eligibleForCommunicate ? ['communicate'] : []),
  ] as Activities[];

  const isInExperimentAiGrammar = !!eligibility?.eligibleForAiBuddies;

  if (isLoadingPointsAndProgress)
    return (
      <S.LoaderWrapper>
        <S.Loader />
      </S.LoaderWrapper>
    );

  return (
    <Page footerStyle="none">
      <Head>
        <title>{intl.formatMessage(msgs.title)} - Memrise</title>
      </Head>
      <BackHeader href={backUrl} />
      {pointsAfter && pointsBefore && progressAfter && progressBefore && (
        <SessionCompletedCelebration
          onClick={/* istanbul ignore next */ () => setShowSessionCompletedCelebration(false)}
          pointsAfter={pointsAfter.points}
          pointsBefore={pointsBefore.points}
          progressAfter={progressAfter}
          progressBefore={progressBefore}
          isOpen={showSessionCompletedCelebration}
        />
      )}
      {pointsAfter && pointsBefore && showLevelAndStageCelebrations && (
        <LevelAndStageCelebrations
          targetLanguageName={languageName}
          pointsBefore={pointsBefore}
          pointsAfter={pointsAfter}
          onContinue={() => {
            setShowLevelAndStageCelebrations(false);
            setHasClickedContinue(true);
          }}
        />
      )}
      <S.FullWidthFloatingPortal>
        <div id="floatingBottomPortalRoot" />
      </S.FullWidthFloatingPortal>
      {hasClickedContinue && !showBuddyRecommendation && (
        <FloatingMiniPlayer
          currentActivity={activity}
          onChangeActivity={setActivity}
          recommendations={recommendations}
          supportedActivities={supportedActivities}
          source={ActivitySource.EOL}
          overlay
          customImmerseRecommendation={Number(contentMediaId)}
          isLoading={isFetchingRecommendations || isLoadingEligibility}
        />
      )}
      <BuddyRecommendationModal
        isOpen={hasClickedContinue && showBuddyRecommendation}
        isLoading={isLoadingBuddies}
        buddies={buddiesData?.buddies || []}
        handleClose={/* istanbul ignore next */ () => setShowedBuddyRecommendation(true)}
      />
      <S.Root data-testid="scenarios-EndOfSession">
        <S.Content>
          <Center $centerAlignItems>
            <S.ContentWrapper $align="center">
              {hasUserScenario && (
                <ScenarioHeader
                  title={levelName}
                  iconSrc={iconSrc}
                  isPremiumScenario={isPremiumScenario}
                  userScenarioCompleted={userScenarioCompleted}
                  showAnimation={userScenarioCompleted}
                  sessionType={SessionType.LEARN}
                />
              )}
              {userScenarioCompleted && (
                <S.Header>{intl.formatMessage(learnCompletedMessage)}</S.Header>
              )}
              <S.ButtonContainer>
                <Button
                  buttonType="primary"
                  buttonSize="large"
                  wide
                  loading={isLoadingBuddies}
                  onClick={() => {
                    if (shouldReturnToPath) {
                      void safePush(backUrl || ROUTES.path);
                      return;
                    }
                    if (
                      pointsAfter &&
                      pointsBefore &&
                      pointsAfter.level.currentLevel > pointsBefore.level.currentLevel
                    ) {
                      setShowLevelAndStageCelebrations(true);
                    } else {
                      setHasClickedContinue(true);
                    }
                  }}
                >
                  {intl.formatMessage(msgs.continue)}
                </Button>
                {isInExperimentAiGrammar && (
                  <LinkButton
                    buttonType="secondary"
                    buttonSize="large"
                    icon="buddyFullWaving"
                    href={{
                      pathname: ROUTES.ai.dialog,
                      query: {
                        type: 'sentence-builder',
                        key: 'learn',
                      },
                    }}
                  >
                    {intl.formatMessage(msgs.buddyButton)}
                  </LinkButton>
                )}
              </S.ButtonContainer>
              {scenarioDetails && (
                <>
                  <ScenarioSummary scenario={scenarioDetails} />
                  <S.ListWrapper>
                    <LearnablesList
                      isLoading={isLoadingScenarioDetails}
                      list={sortedLearnables}
                      isLocked
                    />
                  </S.ListWrapper>
                </>
              )}
            </S.ContentWrapper>
          </Center>
        </S.Content>
        <S.SettingsIconWrapper
          onClick={async () => {
            await safePush(ROUTES.settings.learning);
          }}
          aria-label={intl.formatMessage(msgs.learningSettings)}
        >
          <Icon name="settingsFilled" />
        </S.SettingsIconWrapper>
      </S.Root>
    </Page>
  );
};
